import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PROJECTS_QUERY_KEYS } from '@/entities/project';
import {
  PROJECT_FILE_QUERY_KEYS,
  confirmProjectFileUpload,
  getProjectFile,
  requestProjectFileVersionUpload,
} from '@/entities/project-file';
import { uploadToS3 } from '@/shared/api/upload-to-s3';
import { sanitizeErrorMessage } from '@/lib/upload-queue/error-message';
import { isUserAbort } from '@/lib/upload-queue/is-user-abort';
import { enqueueUpload } from '@/lib/upload-queue/upload-queue-bindings';
import { logger } from '@/lib/utils/logger';

/**
 * Thrown when the latest version the FE has in state no longer matches the
 * server's latest — i.e. someone else uploaded a new version while this user
 * had the file open. Callers should catch this in `onError` and prompt a reload.
 */
export class VersionStaleError extends Error {
  readonly latestVersionId: string;

  constructor(latestVersionId: string) {
    // eslint-disable-next-line i18next/no-literal-string -- sentinel message; the consumer branches on `instanceof VersionStaleError` and shows a translated warning
    super('A newer version of this file exists on the server.');
    this.name = 'VersionStaleError';
    this.latestVersionId = latestVersionId;
  }
}

interface UseUploadProjectFileVersionOptions {
  projectId: string;
  /** Folder the file lives in, for cache invalidation. */
  folderId?: string;
}

export interface UploadProjectFileVersionInput {
  file: File;
  fileId: string;
  /**
   * Latest version the FE believes exists (from file detail or the conflict
   * check). Compared against the server's freshly-fetched `latestVersionId`;
   * mismatch throws `VersionStaleError`. Pass null to skip the check.
   */
  knownLatestVersionId: string | null;
}

export function useUploadProjectFileVersion(options: UseUploadProjectFileVersionOptions) {
  const queryClient = useQueryClient();
  const { projectId, folderId } = options;

  return useMutation({
    mutationFn: async ({ file, fileId, knownLatestVersionId }: UploadProjectFileVersionInput) => {
      const handle = enqueueUpload({ file, source: 'folder', projectId, folderId });

      const fresh = await getProjectFile(projectId, fileId);
      if (fresh.latestVersionId && fresh.latestVersionId !== knownLatestVersionId) {
        throw new VersionStaleError(fresh.latestVersionId);
      }

      try {
        const uploadResponse = await requestProjectFileVersionUpload(projectId, fileId, {
          fileSize: file.size,
          contentType: file.type,
        });

        await uploadToS3(
          uploadResponse.uploadUrl,
          uploadResponse.uploadFields,
          file,
          handle.abortSignal,
          handle.onProgress,
        );

        await confirmProjectFileUpload(projectId, fileId, uploadResponse.versionId);

        handle.markSuccess();
        return uploadResponse;
      } catch (err) {
        if (isUserAbort(err)) {
          throw err;
        }
        // eslint-disable-next-line i18next/no-literal-string -- pre-sanitize placeholder; `sanitizeErrorMessage` returns the translated fallback
        const raw = err instanceof Error ? err.message : 'Upload failed';
        logger.error('[upload-project-file-version] upload failed', { file: file.name, raw, err });
        handle.markFailed(sanitizeErrorMessage(raw));
        throw err;
      }
    },
    onSuccess: (_data, { fileId }) => {
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.detail(projectId, fileId),
      });
      void queryClient.invalidateQueries({
        queryKey: PROJECT_FILE_QUERY_KEYS.versions(projectId, fileId),
      });

      if (folderId) {
        void queryClient.invalidateQueries({
          queryKey: PROJECTS_QUERY_KEYS.folders.files(projectId, folderId),
        });
      }
    },
  });
}
